import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import PriceOfferAssignForm, { PriceOfferAssignFormData } from 'components/forms/price-offer/PriceOfferAssignForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PRICE_OFFERS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to assign a new price offer.
 */
export default function AddPriceOfferPage() {
  const { getAction } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId') ?? '';

  const initialValues = useMemo<PriceOfferAssignFormData>(
    () => ({
      deviceId,
      assignedTo: [],
      description: '',
      note: '',
    }),
    [deviceId]
  );

  /**
   * Creates the price offer assignment.
   */
  const submit = async ({ deviceId, assignedTo, description, note }: PriceOfferAssignFormData) => {
    try {
      const action = getAction('PriceOfferCreateBulk');
      const response = await action({
        payload: {
          deviceId: Number(deviceId),
          assignedTo: assignedTo.map(Number),
          description: description.trim() === '' ? undefined : description.trim(),
          note: note.trim() === '' ? undefined : note.trim(),
        },
      });
      const createdCount = response.priceOfferIds.length;

      showNotification({
        color: SUCCESS_NOTIFICATION_COLOR,
        title: createdCount === 1 ? 'Cenová ponuka bola vytvorená' : 'Cenové ponuky boli vytvorené',
        message:
          createdCount === 1
            ? 'Vypracovateľ bol informovaný e-mailom.'
            : `Vypracovatelia boli informovaní e-mailom. Počet vytvorených ponúk: ${createdCount}.`,
      });

      navigate(PRICE_OFFERS_PAGE_PATH.original);
    } catch (error) {
      panic(error as Error);
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[{ title: 'Cenové ponuky', link: PRICE_OFFERS_PAGE_PATH.original }, { title: 'Nová cenová ponuka' }]}
      title="Nová cenová ponuka"
    >
      <PriceOfferAssignForm
        key={deviceId || 'empty'}
        initialValues={initialValues}
        onSubmit={submit}
        readOnly={{ deviceId: Boolean(deviceId) }}
      />
    </DashboardLayout>
  );
}
